import { CancelToken } from 'axios';
import { RequestsPerPage } from '../types';
import axios from '../../utils/axios';
import { PostContractData, PutContractData, StudentData } from '../../views/Student/StudentListView/types';

const getStudentsPerPage = async ({
  page, limit, searchValue, sort, year,
}: RequestsPerPage & { year?: number }) => {
  let url = `/api/students?page=${page}&limit=${limit}&searchValue=${searchValue}&sort=${sort}`;
  if (year) {
    url += `&year=${year}`;
  }
  const response = await axios.get(url);

  return response.data;
};

const getStudentById = async (studentId: string) => {
  const response = await axios.get(`/api/students/${studentId}`);

  return response.data;
};

const getEnrollmentSummaryByStudentId = async (studentId: string) => {
  const response = await axios.get(`/api/students/${studentId}/enrollment-summary`);

  return response.data;
};

const putUpdateEnrollmentSummaryByStudentId = async (
  studentId: string,
  data: Record<string, unknown>,
) => {
  const response = await axios.put(`/api/students/${studentId}/enrollment-summary`, data);

  return response.data;
};

const postCreateStudent = async ({
  rut,
  phoneNumber,
  name,
  lastname,
  email,
  year,
}: StudentData) => {
  const response = await axios.post('/api/students', {
    rut,
    phoneNumber,
    name,
    lastname,
    email,
    year,
  });

  return response.data;
};

const putUpdateStudent = async ({
  id,
  phoneNumber,
  name,
  lastname,
  rut,
  email,
  onlineEnabled,
}: {
  id: string,
  phoneNumber: string,
  name: string,
  lastname: string,
  rut: string,
  email: string,
  onlineEnabled: boolean,
}) => {
  const response = await axios.put(`/api/students/${id}`, {
    phoneNumber,
    name,
    lastname,
    rut,
    email,
    onlineEnabled,
  });

  return response.data;
};

const deleteStudent = async (studentId: string) => {
  const response = await axios.delete(`/api/students/${studentId}`);

  return response.data;
};

const restoreStudent = async (studentId: string) => {
  const response = await axios.put(`/api/students/${studentId}/restore`);

  return response.data;
};

const getStudentContractsByStudentId = async (studentId: string, cancelToken?: CancelToken) => {
  const response = await axios.get(`/api/students-contracts/${studentId}`, { cancelToken });

  return response.data;
};

const getContractById = async (contractId: string) => {
  const response = await axios.get(`/api/contracts/${contractId}`);

  return response.data;
};

const postContract = async ({
  studentId,
  startDate,
  endDate,
  tuition,
  tuitionDiscount,
  tuitionAgreement,
  tuitionPaymentType,
  feeDiscountType,
  feeDiscount,
  feeAdditionalDiscount,
  feePaymentType,
  feePaymentTypeDiscount,
  studentProgramInfo,
  comment,
  installments,
  status,
  academicYear,
}: PostContractData) => {
  const response = await axios.post('/api/contracts', {
    studentId,
    startDate,
    endDate,
    tuition,
    tuitionDiscount,
    tuitionAgreement,
    tuitionPaymentType,
    feeDiscountType,
    feeDiscount,
    feeAdditionalDiscount,
    feePaymentType,
    feePaymentTypeDiscount,
    studentProgramInfo,
    comment,
    installments,
    status,
    academicYear,
  });

  return response.data;
};

const putContract = async ({
  contractId,
  studentId,
  startDate,
  endDate,
  tuition,
  tuitionDiscount,
  tuitionAgreement,
  tuitionPaymentType,
  feeDiscountType,
  feeDiscount,
  feeAdditionalDiscount,
  feePaymentType,
  feePaymentTypeDiscount,
  feeDiscountInvalidPaymentMethods,
  feeDiscountValidDays,
  studentProgramInfo,
  comment,
  installments,
  academicYear,
}: PutContractData & {
  feeDiscountInvalidPaymentMethods?: string | null;
  feeDiscountValidDays?: number | null;
  academicYear?: number;
}) => {
  const response = await axios.put(`/api/contracts/${contractId}`, {
    studentId,
    startDate,
    endDate,
    tuition,
    tuitionDiscount,
    tuitionAgreement,
    tuitionPaymentType,
    feeDiscountType,
    feeDiscount,
    feeAdditionalDiscount,
    feePaymentType,
    feePaymentTypeDiscount,
    feeDiscountInvalidPaymentMethods,
    feeDiscountValidDays,
    studentProgramInfo,
    comment,
    installments,
    academicYear,
  });

  return response.data;
};

const putStudyPlan = async ({
  contractId,
  studentId,
  startDate,
  endDate,
  tuition,
  tuitionDiscount,
  tuitionAgreement,
  feeDiscount,
  feeDiscountType,
  feeAdditionalDiscount,
  feePaymentTypeDiscount,
  feeDiscountInvalidPaymentMethods,
  feeDiscountValidDays,
  studentProgramInfo,
  flow,
}: {
  contractId: string;
  studentId: string;
  startDate: Date;
  endDate: Date;
  tuition: number;
  tuitionDiscount: number;
  tuitionAgreement: string | null;
  feeDiscount: number;
  feeDiscountType?: string | null;
  feeAdditionalDiscount: number;
  feePaymentTypeDiscount: number;
  feeDiscountInvalidPaymentMethods?: string | null;
  feeDiscountValidDays?: number | null;
  studentProgramInfo: Array<{
    programId: string;
    feeDiscount: number;
    comment: string | null;
  }>;
  flow?: string;
}) => {
  const response = await axios.put(`/api/contracts/${contractId}/study-plan`, {
    studentId,
    startDate,
    endDate,
    tuition,
    tuitionDiscount,
    tuitionAgreement,
    feeDiscount,
    feeDiscountType,
    feeAdditionalDiscount,
    feePaymentTypeDiscount,
    feeDiscountInvalidPaymentMethods,
    feeDiscountValidDays,
    studentProgramInfo,
    flow,
  });

  return response.data;
};

const getStudentSectionsByStudentId = async (
  studentId: string,
  cancelToken?: CancelToken,
  year?: number,
) => {
  let url = `/api/students-sections/${studentId}`;
  if (year) {
    url += `?year=${year}`;
  }
  const response = await axios.get(url, { cancelToken });

  return response.data;
};

const getProgramSectionsByProgramId = async (programId: string, searchValue: string) => {
  const response = await axios.get(`/api/students-program-sections/${programId}?searchValue=${searchValue}`);

  return response.data;
};

const putProgramSections = async ({
  studentId,
  programId,
  sectionId,
}: {
  studentId: string,
  programId: string,
  sectionId: string,
}) => {
  const response = await axios.put('/api/students-program-sections', {
    studentId,
    programId,
    sectionId,
  });

  return response.data;
};

const getTestsInformationByStudentId = async (
  studentId: string,
  cancelToken?: CancelToken,
  year?: number,
) => {
  let url = `/api/students-tests/${studentId}`;
  if (year) {
    url += `?year=${year}`;
  }
  const response = await axios.get(url, { cancelToken });

  return response.data;
};

const getContentsByStudentId = async (
  studentId: string,
  searchValue: string,
  filterValue: string,
  cancelToken?: CancelToken,
  year?: number,
) => {
  let url = `/api/student-contents/${studentId}?searchValue=${searchValue}&filterValue=${filterValue}`;
  if (year) {
    url += `&year=${year}`;
  }
  const response = await axios.get(url, { cancelToken });

  return response.data;
};

const getFeedbacksByStudentId = async (
  studentId: string,
  cancelToken?: CancelToken,
  year?: number,
) => {
  let url = `/api/student-feedbacks/${studentId}`;
  if (year) {
    url += `?year=${year}`;
  }
  const response = await axios.get(url, { cancelToken });

  return response.data;
};

const getFeedbacksTestsByStudentId = async (studentId: string, programId: string) => {
  const response = await axios.get(`/api/student-feedbacks/student/${studentId}/program/${programId}`);

  return response.data;
};

const getFeedbacksProgramsByStudentId = async (studentId: string, year?: number) => {
  let url = `/api/student-feedbacks/student/${studentId}/programs`;
  if (year) {
    url += `?year=${year}`;
  }
  const response = await axios.get(url);

  return response.data;
};

const postFeedbacks = async ({
  studentId,
  programId,
  minQuestions,
  maxQuestions,
  selectedTestsIds,
  name,
}: {
  studentId: string,
  programId: string,
  minQuestions: number,
  maxQuestions: number,
  selectedTestsIds: string[],
  name?: string,
}) => {
  const response = await axios.post(`/api/student-feedbacks/${studentId}`, {
    minQuestions,
    maxQuestions,
    programId,
    selectedTestsIds,
    name,
  });

  return response.data;
};

const downloadTest = async (url: string) => {
  const response = await axios.post('/api/student-tests/download-test', { url });

  return response.data;
};

const getTestsQuestionsByStudentId = async (studentId: string, testId: string) => {
  const response = await axios.get(`/api/student-test-questions/student/${studentId}/test/${testId}`);

  return response.data;
};

const getAssistancesInformationByStudentId = async (
  studentId: string,
  cancelToken?: CancelToken,
  year?: number,
) => {
  let url = `/api/student-assistances/student/${studentId}`;
  if (year) {
    url += `?year=${year}`;
  }
  const response = await axios.get(url, { cancelToken });

  return response.data;
};

const getStudentRepresentativesByStudentId = async (
  studentId: string,
  cancelToken: CancelToken,
) => {
  const response = await axios.get(`/api/student-representatives/${studentId}`, { cancelToken });

  return response.data;
};

const postRepresentativeStudentAssociation = async ({
  studentId,
  representativeId,
  relationToStudent,
}: {
  studentId: string,
  representativeId: string,
  relationToStudent: string,
}) => {
  const response = await axios.post('/api/student-representatives', {
    studentId,
    representativeId,
    relationToStudent,
  });

  return response.data;
};

const putRepresentativeStudentAndAssociation = async ({
  studentId,
  representativeId,
  relationToStudent,
  rut,
  phoneNumber,
  name,
  lastname,
  email,
  birthDate,
  nationality,
  maritalStatus,
  profession,
  address,
  commune,
}: {
  studentId: string,
  representativeId: string,
  relationToStudent: string,
  rut: string,
  phoneNumber: string,
  name: string,
  lastname: string,
  email: string,
  birthDate: Date | string,
  nationality: string,
  maritalStatus: string,
  profession: string,
  address: string,
  commune: string,
}) => {
  const response = await axios.put('/api/student-representatives', {
    studentId,
    representativeId,
    relationToStudent,
    rut,
    phoneNumber,
    name,
    lastname,
    email,
    birthDate,
    nationality,
    maritalStatus,
    profession,
    address,
    commune,
  });

  return response.data;
};

const deleteStudentRepresentativeAssociation = async (studentRepresentativeId: string) => {
  const response = await axios.delete(`/api/student-representatives/${studentRepresentativeId}`);

  return response.data;
};

const getStudentsFromRepresentative = async (representativeId: string) => {
  const response = await axios.get(`/api/student-representatives/representative/${representativeId}`);

  return response.data;
};

const postStudentAnswers = async ({
  studentId,
  testId,
  answers,
}: {
  studentId: string;
  testId: string;
  answers: {
    testQuestionId: string;
    questionNumber: string;
    answer: string;
  }[];
}) => {
  const response = await axios.post('/api/student-tests/submit-answers', {
    studentId,
    testId,
    answers,
  });

  return response.data;
};

const putStudentSavedAnswers = async ({
  studentId,
  testId,
  answers,
}: {
  studentId: string;
  testId: string;
  answers: {
    testQuestionId: string;
    questionNumber: string;
    answer: string;
  }[];
}) => {
  const response = await axios.put('/api/student-tests/update-answers', {
    studentId,
    testId,
    answers,
  });

  return response.data;
};

const putStudentOnlineStatus = async (studentId: string, onlineEnabled: boolean) => {
  const response = await axios.put('/api/students/online-enabled/status', {
    studentId,
    onlineEnabled,
  });

  return response.data;
};

const postGenerateContractDocument = async (
  { contractId, contractDocumentId }: { contractId: string, contractDocumentId: string },
) => {
  const response = await axios.post('/api/students/documents/generate', { contractId: contractId.toString(), contractDocumentId: contractDocumentId.toString() });

  return response.data;
};

const postDownloadDocument = async (url: string) => {
  const response = await axios.post('/api/students/documents/download', { url });

  return response.data;
};

const postSendDocument = async (
  studentId: string, contractDocumentId: string, documentUrl: string,
) => {
  const response = await axios.post('/api/students/documents/send', {
    studentId: studentId.toString(),
    contractDocumentId: contractDocumentId.toString(),
    documentUrl,
  });

  return response.data;
};

const deleteDocument = async (contractDocumentId: string) => {
  const response = await axios.delete(`/api/students/documents/delete/${contractDocumentId}`);

  return response.data;
};

const postUploadAnnexDocument = async (file: File, contractId: string) => {
  const formData = new FormData();

  if (file) {
    formData.append('file', file);
    formData.set('contractId', contractId);
  }
  const response = await axios.post('/api/students/documents/upload', formData);

  return response.data;
};

const buildDownloadUrl = (path: string, year?: number) => (year ? `${path}?year=${year}` : path);

const studentsCSVDownload = async (year?: number) => {
  const response = await axios.get(buildDownloadUrl('/api/download/students', year));

  return response.data;
};

const studentAssistancesCSVDownload = async (year?: number) => {
  const response = await axios.get(buildDownloadUrl('/api/download/students-assistances', year));

  return response.data;
};

const studentTestsCSVDownload = async (year?: number) => {
  const response = await axios.get(buildDownloadUrl('/api/download/students-tests', year));

  return response.data;
};

const studentTestsResultsCSVDownload = async (year?: number) => {
  const response = await axios.get(buildDownloadUrl('/api/download/students-results', year));

  return response.data;
};

const postCreateStudentWithRepresentatives = async ({
  student,
  representative,
  sustainer,
}: {
  student: {
    phoneNumber: string;
    rut: string;
    name: string;
    lastname: string;
    email: string;
    birthDate?: string;
    stage?: string;
    schoolId?: number | null;
    otherSchoolName?: string | null;
    testsToTake?: string[];
    testsComment?: string;
    preferredLocationIds?: number[] | null;
    desiredClassYear?: string;
    inscriptionYear?: number | null;
    leadSource?: string;
    leadName?: string;
    ambassadorCode?: string;
  };
  representative: {
    phoneNumber: string;
    rut: string;
    name: string;
    lastname: string;
    email: string;
    birthDate?: string;
    nationality?: string;
    maritalStatus?: string;
    profession?: string;
    address?: string;
    commune?: string;
    otherCommune?: string | null;
  };
  sustainer: {
    phoneNumber: string;
    rut: string;
    name: string;
    lastname: string;
    email: string;
    birthDate?: string;
    nationality?: string;
    maritalStatus?: string;
    profession?: string;
    address?: string;
    commune?: string;
    otherCommune?: string | null;
  };
}) => {
  const response = await axios.post('/api/students/enrollment', {
    student,
    representative,
    sustainer,
  });

  return response.data;
};

const validateEmailExists = async (
  email: string,
  context?: 'student' | 'representative',
  identityData?: {
    rut?: string;
  },
) => {
  const params: { email: string, context?: 'student' | 'representative', rut?: string } = {
    email,
    context,
  };

  if (identityData) {
    params.rut = identityData.rut;
  }

  const response = await axios.get('/api/validate-email', {
    params,
  });

  return response.data;
};

const putAcceptStudyPlan = async (studentId: string) => {
  const response = await axios.put(`/api/enrollment/accept-study-plan/${studentId}`);

  return response.data;
};

const putEnableEnrollment = async (studentId: string) => {
  const response = await axios.put(`/api/enrollment/enable-enrollment/${studentId}`);

  return response.data;
};

const putConfirmPayment = async (studentId: string) => {
  const response = await axios.put(`/api/enrollment/confirm-payment/${studentId}`);

  return response.data;
};

export {
  getStudentsPerPage,
  getStudentById,
  postCreateStudent,
  putUpdateStudent,
  deleteStudent,
  restoreStudent,
  getStudentContractsByStudentId,
  getContractById,
  postContract,
  putContract,
  putStudyPlan,
  putAcceptStudyPlan,
  putEnableEnrollment,
  putConfirmPayment,
  getStudentSectionsByStudentId,
  getProgramSectionsByProgramId,
  putProgramSections,
  getTestsInformationByStudentId,
  getContentsByStudentId,
  getFeedbacksByStudentId,
  getFeedbacksTestsByStudentId,
  getFeedbacksProgramsByStudentId,
  postFeedbacks,
  downloadTest,
  getTestsQuestionsByStudentId,
  getAssistancesInformationByStudentId,
  getStudentRepresentativesByStudentId,
  postRepresentativeStudentAssociation,
  putRepresentativeStudentAndAssociation,
  deleteStudentRepresentativeAssociation,
  getStudentsFromRepresentative,
  postStudentAnswers,
  putStudentSavedAnswers,
  putStudentOnlineStatus,
  postGenerateContractDocument,
  postDownloadDocument,
  postSendDocument,
  deleteDocument,
  studentsCSVDownload,
  studentAssistancesCSVDownload,
  studentTestsCSVDownload,
  studentTestsResultsCSVDownload,
  postUploadAnnexDocument,
  postCreateStudentWithRepresentatives,
  validateEmailExists,
  getEnrollmentSummaryByStudentId,
  putUpdateEnrollmentSummaryByStudentId,
  postContractComment,
  getContractComments,
  putStudentProgramRetiro,
};

const postContractComment = async (contractId: string, comment: string) => {
  const response = await axios.post(`/api/contracts/${contractId}/comments`, {
    comment,
  });
  return response.data;
};

const getContractComments = async (contractId: string) => {
  const response = await axios.get(`/api/contracts/${contractId}/comments`);
  return response.data;
};

const putStudentProgramRetiro = async (
  contractId: string,
  studentProgramId: string,
  feeRetained: number,
) => {
  const response = await axios.put(
    `/api/contracts/${contractId}/student-programs/${studentProgramId}/retiro`,
    { feeRetained },
  );
  return response.data;
};
